import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listPortalAsns, createPortalAsn, getPortalOrderFormData, bulkCreatePortalAsns,
} from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Plus, Loader2, Upload } from "lucide-react";
import { CsvImportDialog } from "@/components/csv-import-dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/inbound")({ component: PortalInbound });

type AsnLineDraft = { skuId: string; qty: number; lot: string };

function PortalInbound() {
  const qc = useQueryClient();
  const fetchAsns = useServerFn(listPortalAsns);
  const fetchForm = useServerFn(getPortalOrderFormData);
  const createFn = useServerFn(createPortalAsn);
  const bulkFn = useServerFn(bulkCreatePortalAsns);

  const { data: asns, isLoading } = useQuery({
    queryKey: ["portal-asns"],
    queryFn: () => runServerFn(() => fetchAsns(), "Loading ASNs"),
  });
  const { data: form } = useQuery({
    queryKey: ["portal-order-form"],
    queryFn: () => runServerFn(() => fetchForm(), "Loading form"),
  });

  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState("");
  const [carrier, setCarrier] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [lines, setLines] = useState<AsnLineDraft[]>([{ skuId: "", qty: 1, lot: "" }]);
  const [submitting, setSubmitting] = useState(false);

  const update = (i: number, patch: Partial<AsnLineDraft>) =>
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form?.customer || !facilityId) return toast.error("Pick a facility");
    const clean = lines.filter((l) => l.skuId && l.qty > 0);
    setSubmitting(true);
    try {
      await runServerFn(() => createFn({
        data: {
          tenantId: form.customer!.tenant_id,
          customerId: form.customer!.id,
          facilityId,
          reference: reference.trim(),
          carrier: carrier || null,
          expectedAt: expectedAt ? new Date(expectedAt).toISOString() : null,
          lines: clean.length > 0
            ? clean.map((l) => ({ skuId: l.skuId, qty: l.qty, lot: l.lot || null }))
            : undefined,
        },
      }), "Creating ASN");
      toast.success("ASN created");
      setOpen(false); setReference(""); setCarrier(""); setExpectedAt(""); setFacilityId("");
      setLines([{ skuId: "", qty: 1, lot: "" }]);
      qc.invalidateQueries({ queryKey: ["portal-asns"] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // CSV import: group rows by reference, resolve sku_code → id
  const skuByCode = new Map((form?.skus ?? []).map((s) => [s.sku_code.toLowerCase(), s.id]));

  return (
    <>
      <PortalPageHeader
        title="Inbound (ASNs)"
        description="Advance shipment notices for inbound deliveries."
        actions={
          <div className="flex gap-2 flex-wrap">
            <CsvImportDialog
              trigger={<Button size="sm" variant="outline" disabled={!form?.customer}><Upload className="h-4 w-4 mr-1" />Import ASNs</Button>}
              title="Import ASNs"
              templateHeaders={["reference", "carrier", "expected_at", "sku_code", "qty", "lot"]}
              sampleRow={["ASN-1001", "UPS", "2026-06-01", "SKU-001", "100", "LOT-A"]}
              validateRow={(r) => {
                if (!r.reference) return { ok: false, error: "reference required" };
                if (r.sku_code && !skuByCode.has(r.sku_code.toLowerCase())) {
                  return { ok: false, error: `Unknown sku_code: ${r.sku_code}` };
                }
                const qty = r.qty ? parseInt(r.qty, 10) : 0;
                if (r.sku_code && (!qty || qty < 1)) return { ok: false, error: "qty must be ≥1 when sku_code set" };
                return { ok: true, value: r };
              }}
              onImport={async (rows) => {
                if (!form?.customer || !facilityId) {
                  toast.error("Select a facility on the form first, then re-import.");
                  return { inserted: 0, failed: rows.map((_, i) => ({ row: i + 2, error: "facility not selected" })) };
                }
                const grouped = new Map<string, { reference: string; carrier?: string | null; expectedAt?: string | null; lines: { skuId: string; qty: number; lot: string | null }[] }>();
                for (const r of rows) {
                  const key = r.reference.trim();
                  if (!grouped.has(key)) {
                    grouped.set(key, {
                      reference: key,
                      carrier: r.carrier?.trim() || null,
                      expectedAt: r.expected_at?.trim() || null,
                      lines: [],
                    });
                  }
                  if (r.sku_code) {
                    grouped.get(key)!.lines.push({
                      skuId: skuByCode.get(r.sku_code.toLowerCase())!,
                      qty: parseInt(r.qty, 10),
                      lot: r.lot?.trim() || null,
                    });
                  }
                }
                const res = await runServerFn(() => bulkFn({
                  data: {
                    tenantId: form.customer!.tenant_id,
                    customerId: form.customer!.id,
                    facilityId,
                    asns: Array.from(grouped.values()),
                  },
                }), "Importing ASNs");
                return res;
              }}
              onDone={() => qc.invalidateQueries({ queryKey: ["portal-asns"] })}
            />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" disabled={!form?.customer}><Plus className="h-4 w-4 mr-1" />New ASN</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Create ASN</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Reference</Label>
                      <Input required value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ASN-1001" /></div>
                    <div className="space-y-2"><Label>Facility</Label>
                      <Select value={facilityId} onValueChange={setFacilityId}>
                        <SelectTrigger><SelectValue placeholder="Pick facility" /></SelectTrigger>
                        <SelectContent>{form?.facilities?.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Carrier</Label>
                      <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS / FedEx" /></div>
                    <div className="space-y-2"><Label>Expected at</Label>
                      <Input type="date" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} /></div>
                  </div>
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Expected lines (optional)</Label>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setLines((p) => [...p, { skuId: "", qty: 1, lot: "" }])}><Plus className="h-3 w-3 mr-1" />Add line</Button>
                    </div>
                    {lines.map((l, i) => (
                      <div key={i} className="grid grid-cols-[1fr_90px_90px_36px] gap-2">
                        <Select value={l.skuId} onValueChange={(v) => update(i, { skuId: v })}>
                          <SelectTrigger><SelectValue placeholder="SKU" /></SelectTrigger>
                          <SelectContent>{form?.skus?.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name ?? ""}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" min={1} value={l.qty} onChange={(e) => update(i, { qty: parseInt(e.target.value) || 1 })} />
                        <Input placeholder="Lot" value={l.lot} onChange={(e) => update(i, { lot: e.target.value })} />
                        <Button type="button" size="icon" variant="ghost" disabled={lines.length === 1}
                          onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>×</Button>
                      </div>
                    ))}
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={submitting}>
                      {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="p-4 sm:p-6">
        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Reference</TableHead><TableHead>Carrier</TableHead>
              <TableHead>Expected</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Created</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading ? (<TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>)
              : asns && asns.length > 0 ? asns.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono font-medium">
                    <Link to="/portal/inbound/$asnId" params={{ asnId: a.id }} className="text-primary hover:underline">
                      {a.reference}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{a.carrier ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{a.expected_at ? new Date(a.expected_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell><StatusPill tone={statusTone(a.status)}>{a.status}</StatusPill></TableCell>
                  <TableCell className="text-right text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              )) : (<TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No ASNs yet.</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}